export type AffiliateCheckoutStatus = 'completed' | 'payment_failed' | 'expired' | 'pending';

interface PollAffiliateCheckoutStatusOptions {
  apiUrl: string;
  sessionId: string;
  getToken: () => Promise<string | null>;
  fetcher?: typeof fetch;
  wait?: (milliseconds: number) => Promise<void>;
  attempts?: number;
  requestTimeoutMs?: number;
}

interface ContinueToAffiliateCheckoutOptions {
  storage: Pick<Storage, 'setItem'> | null;
  application: unknown;
  sessionId: string;
  checkoutUrl: string;
  navigate: (checkoutUrl: string) => void;
}

interface SessionStorageProvider {
  readonly sessionStorage?: Storage;
}

export function getAffiliateCheckoutStorage(
  browser: SessionStorageProvider = window
): Storage | null {
  try {
    return browser.sessionStorage ?? null;
  } catch {
    return null;
  }
}

export function setAffiliateCheckoutStorage(
  storage: Pick<Storage, 'setItem'> | null,
  key: string,
  value: string
): void {
  try {
    storage?.setItem(key, value);
  } catch {
    // Checkout state is optional because Stripe returns the session ID in its URL.
  }
}

export function readAffiliateCheckoutStorage(
  storage: Pick<Storage, 'getItem'> | null,
  key: string
): string | null {
  try {
    return storage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

export function removeAffiliateCheckoutStorage(
  storage: Pick<Storage, 'removeItem'> | null,
  key: string
): void {
  try {
    storage?.removeItem(key);
  } catch {
    // Cleanup is best-effort when browser storage is unavailable.
  }
}

export function continueToAffiliateCheckout({
  storage,
  application,
  sessionId,
  checkoutUrl,
  navigate,
}: ContinueToAffiliateCheckoutOptions): void {
  setAffiliateCheckoutStorage(storage, 'affiliateApplication', JSON.stringify(application));
  setAffiliateCheckoutStorage(storage, 'affiliateCheckoutSessionId', sessionId);

  navigate(checkoutUrl);
}

export async function pollAffiliateCheckoutStatus({
  apiUrl,
  sessionId,
  getToken,
  fetcher = fetch,
  wait = (milliseconds) => new Promise((resolve) => window.setTimeout(resolve, milliseconds)),
  attempts = 8,
  requestTimeoutMs = 10_000,
}: PollAffiliateCheckoutStatusOptions): Promise<AffiliateCheckoutStatus> {
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      const token = await getToken();
      const response = await fetcher(
        `${apiUrl}/public/affiliate-application/checkout/${encodeURIComponent(sessionId)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          signal: AbortSignal.timeout(requestTimeoutMs),
        }
      );
      if (response.ok) {
        const result = (await response.json()) as { data?: { status?: string } };
        if (result.data?.status === 'completed') return 'completed';
        if (result.data?.status === 'payment_failed' || result.data?.status === 'expired') {
          return result.data.status;
        }
      }
    } catch {
      // Redirect-time auth, network, and response failures are transient.
    }
    await wait(750);
  }

  return 'pending';
}
